import { useState } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/auth/auth-provider';
import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { PlaybookCatalogue, canRunTier } from '@/components/fleet/playbook-catalogue';
import { SurveyForm } from '@/components/fleet/survey-form';
import { TierBadge } from '@/components/fleet/tier-badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useProvisioningPlaybooks } from '@/hooks/use-provisioning-playbooks';
import { useRunPlaybook } from '@/hooks/use-run-playbook';
import type { ServerDetail } from '@/types/fleet';
import type { Playbook, SurveyField } from '@/types/provisioning';

/**
 * The survey field that scopes a playbook to one server, if it has one —
 * e.g. `target_server`, `target_vpn_servers`. A playbook with no such field
 * is a fleet-wide operation, not a per-server one.
 */
function serverTargetField(playbook: Playbook): SurveyField | undefined {
  return playbook.survey.find(
    (field) => /target/i.test(field.name) && /server|node/i.test(field.name),
  );
}

/** True when the playbook can be run against a single server. */
function isServerScoped(playbook: Playbook): boolean {
  return serverTargetField(playbook) !== undefined;
}

/**
 * Pre-fill the playbook's server-target survey field with this server's name,
 * so running it from the server's page is scoped to it.
 */
function scopeToServer(playbook: Playbook, serverName: string): Playbook {
  const target = serverTargetField(playbook);
  if (!target) return playbook;

  return {
    ...playbook,
    survey: playbook.survey.map((field) =>
      field.name === target.name ? { ...field, default: serverName } : field,
    ),
  };
}

/**
 * The full playbook catalogue, runnable per server.
 *
 * Lists every playbook (grouped by tier by `PlaybookCatalogue`); selecting one
 * opens its survey form in a dialog with `target_server` pre-filled. Reuses
 * the same run machinery as the former standalone Playbooks page — DESTRUCTIVE
 * playbooks still go through the type-the-name confirm gate.
 */
export function ServerPlaybooksCard({ server }: { server: ServerDetail }): JSX.Element {
  const { isAdmin } = useAuth();
  const playbooksQuery = useProvisioningPlaybooks();
  const runMutation = useRunPlaybook();

  // Only playbooks that can be scoped to a single server are shown here.
  const serverPlaybooks = (playbooksQuery.data ?? []).filter(isServerScoped);

  const [selected, setSelected] = useState<Playbook | null>(null);
  const [pendingArgs, setPendingArgs] = useState<Record<string, unknown> | null>(null);

  function fire(playbook: Playbook, args: Record<string, unknown>, reason?: string): void {
    runMutation.mutate(
      { playbookId: playbook.id, body: reason ? { args, reason } : { args } },
      {
        onSuccess: ({ task_id }) => {
          toast.success(`${playbook.name} queued (task #${task_id}).`);
          setSelected(null);
        },
        onError: (err) => toast.error(`Couldn't start ${playbook.name}: ${err.message}`),
      },
    );
  }

  function submit(args: Record<string, unknown>): void {
    if (!selected) return;

    // DESTRUCTIVE playbooks defer to the confirm dialog (reason + type-name).
    if (selected.tier === 'DESTRUCTIVE') {
      setPendingArgs(args);
      return;
    }

    fire(selected, args);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Playbooks</CardTitle>
      </CardHeader>
      <CardContent>
        {playbooksQuery.isLoading && (
          <p className="text-sm text-muted-foreground">Loading catalogue…</p>
        )}
        {playbooksQuery.isError && (
          <p className="text-sm text-destructive">Couldn't load the playbook catalogue.</p>
        )}
        {playbooksQuery.data &&
          (serverPlaybooks.length > 0 ? (
            <PlaybookCatalogue
              playbooks={serverPlaybooks}
              selectedId={selected?.id ?? null}
              isAdmin={isAdmin}
              onSelect={setSelected}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              No server-scoped playbooks in the catalogue.
            </p>
          ))}
      </CardContent>

      <Dialog
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent>
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <DialogTitle>{selected.name}</DialogTitle>
                  <TierBadge tier={selected.tier} />
                </div>
              </DialogHeader>
              {selected.description && (
                <p className="text-sm text-muted-foreground">{selected.description}</p>
              )}
              {canRunTier(selected.tier, isAdmin) ? (
                <SurveyForm
                  playbook={scopeToServer(selected, server.name)}
                  submitting={runMutation.isPending}
                  onSubmit={submit}
                />
              ) : (
                <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Tier {selected.tier} requires the admin role.
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingArgs !== null}
        onOpenChange={(open) => {
          if (!open) setPendingArgs(null);
        }}
        title={selected ? `Run ${selected.name}` : 'Run playbook'}
        description={`This is a DESTRUCTIVE operation and cannot be undone. ${
          selected?.description ?? ''
        }`}
        confirmLabel={selected ? `Run ${selected.name}` : 'Run'}
        variant="destructive"
        reasonRequired
        confirmPhrase={selected?.name ?? ''}
        pending={runMutation.isPending}
        onConfirm={(reason) => {
          if (selected && pendingArgs) {
            fire(selected, pendingArgs, reason);
            setPendingArgs(null);
          }
        }}
      />
    </Card>
  );
}
